import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import config from "../../../config.json";

const getHashToken = () => {
  const hash = window.location.hash || "";
  const queryIndex = hash.indexOf("?");
  if (queryIndex === -1) return null;
  return new URLSearchParams(hash.slice(queryIndex + 1)).get("token");
};

export default function AcceptFranchiseInvite() {
  const [searchParams] = useSearchParams();
  const { token: pathToken } = useParams();
  const token = searchParams.get("token") || pathToken || getHashToken();
  const navigate = useNavigate();

  const [inviteInfo, setInviteInfo] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);

  useEffect(() => {
    const verifyInvite = async () => {
      if (!token) {
        setError("Invalid or missing invitation token.");
        setVerifying(false);
        return;
      }

      try {
        const response = await axios.get(
          `${config.dev}/franchise/invite/verify/${encodeURIComponent(token)}`
        );
        setInviteInfo(response.data?.data || null);
      } catch (err) {
        setError(err.response?.data?.error || "Invalid or expired invitation.");
      } finally {
        setVerifying(false);
      }
    };

    void verifyInvite();
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    if (!password || !confirmPassword) {
      setError("Please fill in both password fields.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${config.dev}/franchise/invite/accept`, {
        token,
        password,
        confirmPassword,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
      });

      setSuccess(response.data.message || "Invitation accepted successfully.");
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to accept invitation.");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="reset-password-container">
        <p>Verifying invitation...</p>
      </div>
    );
  }

  return (
    <div className="reset-password-container">
      <h2>Accept franchise invitation</h2>

      {inviteInfo ? (
        <div className="alert alert-info">
          <p className="mb-1">
            Franchise: <strong>{inviteInfo.franchiseName}</strong>
          </p>
          <p className="mb-1">
            Code: <strong>{inviteInfo.codeFr}</strong>
          </p>
          <p className="mb-0">
            Email: <strong>{inviteInfo.email}</strong>
          </p>
        </div>
      ) : null}

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {success ? <div className="alert alert-success">{success}</div> : null}

      {!success && inviteInfo ? (
        <form onSubmit={handleSubmit}>
          <div className="mb-2">
            <label htmlFor="firstName" className="form-label">
              First name
            </label>
            <input
              id="firstName"
              className="form-control"
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
            />
          </div>

          <div className="mb-2">
            <label htmlFor="lastName" className="form-label">
              Last name
            </label>
            <input
              id="lastName"
              className="form-control"
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
            />
          </div>

          <div className="mb-2">
            <label htmlFor="password" className="form-label">
              Password
            </label>
            <input
              type="password"
              id="password"
              className="form-control"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={6}
            />
          </div>

          <div className="mb-3">
            <label htmlFor="confirmPassword" className="form-label">
              Confirm password
            </label>
            <input
              type="password"
              id="confirmPassword"
              className="form-control"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              required
              minLength={6}
            />
          </div>

          <button className="btn btn-primary" type="submit" disabled={loading}>
            {loading ? "Accepting..." : "Accept invitation"}
          </button>
        </form>
      ) : null}
    </div>
  );
}
